/*
===============================================================================================================
                                 Copyright 2021-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useEffect, useState, forwardRef, useContext,
} from 'react';
import PropTypes from 'prop-types';
import TextDiv from '@/components/text-div';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import { ThemeContext } from '@/contexts/theme-context';
import { LocaleContext } from '@/contexts/locale-context';
import { TelemetryContext } from '@/contexts/telemetry-context';
import PermissionGate from '@/components/permission-gate';
import { CONTROL_LEVEL } from '@/utilities/rbac/constants';

const RadioButtons = forwardRef(({
  options,
  subTitleOptions,
  values,
  subControls,
  generateControl,
  onChange,
  defaultSelected,
  isHorizontal,
  gapValue,
  showLabels,
  showBorders,
  showSubtitle,
  required,
  title,
  smallTitle,
  inputSelectorId,
  dynamicSubcontrols,
  error, // Passed in automatically, no need to set
  setContent,
  disabled,
  telemetryEvent,
  telemetryValue,
  telemetryCustomProperty,
  ariaLabel,
  permissionId,
  columnCount,
}, ref) => {
  const { primaryColor } = useContext(ThemeContext);
  const { textDirection } = useContext(LocaleContext);
  const telemetryContext = useContext(TelemetryContext);
  const [selectedIndex, setSelectedIndex] = useState(values.indexOf(defaultSelected));
  useEffect(() => {
    setSelectedIndex(values.indexOf(defaultSelected));
  }, [defaultSelected]);

  const onKeyDown = (func) => (event) => {
    if (event.key === 'Enter' && func) {
      func();
    }
  };

  const selection = (index, option) => {
    if (index !== selectedIndex) {
      setSelectedIndex(index);
      setContent(option?.title ? option?.title : option);
      if (onChange) { onChange(index); }
      if (telemetryEvent) {
        telemetryContext?.sendEvent(telemetryEvent, {
          ...telemetryValue && ({ value_changed: telemetryValue }),
          ...telemetryCustomProperty && { [telemetryCustomProperty]: values[index] },
        });
      }
    }
  };

  const isSubControlVisible = (isOptionSelected) => {
    if (dynamicSubcontrols) {
      return isOptionSelected;
    }
    return true;
  };

  const displayOptions = showSubtitle ? subTitleOptions : options;
  const isRtl = textDirection === 'rtl';
  return (
    <>
      <div className={`wrapper${isHorizontal ? ' horizontal' : ''}${columnCount ? ' column-grid' : ''}`} ref={ref}>
        {title && (
          <div className={`${smallTitle ? 'small-title' : 'title'}`}>
            <TextDiv>
              {`${title}${required === true ? '*' : ''}`}
            </TextDiv>
          </div>
        )}
        {displayOptions.map((option, index) => (
          <div key={showSubtitle ? `${inputSelectorId}-${option.title}` : option}>
            <PermissionGate permission={permissionId} whenDenied={CONTROL_LEVEL.disable}>
              <div
                aria-label={`${ariaLabel} ${option}`}
                className={`radio-button${selectedIndex === index ? ' selected' : ''}${disabled[index] && selectedIndex !== index ? ' is--disabled' : ''}`}
                onClick={() => selection(index, option)}
                onKeyDown={onKeyDown(() => selection(index))}
                role="button"
                tabIndex={0}
                id={`${inputSelectorId}-${index}`}
              >
                {showLabels && (
                  <div className={showSubtitle ? 'heading' : ''}>
                    <div className={showSubtitle ? 'label-title' : ''}>
                      <TextDiv>
                        {showSubtitle ? option.title : option}
                      </TextDiv>
                    </div>
                    {showSubtitle
                      && (
                        <div className="label-subtitle">
                          <TextDiv>
                            {option.subTitle}
                          </TextDiv>
                        </div>
                      )}
                  </div>
                )}
                <div className={`button-wrapper ${showBorders ? 'border' : ''}`}>
                  <span className="visual-button" />
                </div>
              </div>
              {subControls?.length > index && generateControl && isSubControlVisible(selectedIndex === index) && (
                <div className="sub-controls">
                  {generateControl(subControls[index], generateControl, selectedIndex === index)}
                </div>
              )}
            </PermissionGate>
          </div>
        ))}
        {!isHorizontal && (
          <div className={`error${error ? ' visible' : ''}`}>
            {error && <TextDiv>{error}</TextDiv>}
          </div>
        )}
      </div>
      {isHorizontal && (
        <div className={`error${error ? ' visible' : ''}`}>
          {error && <TextDiv>{error}</TextDiv>}
        </div>
      )}
      <style jsx>
        {`
          .wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: ${gapValue}px;
          }

          .small-title {
            color: var( --color-title-grey);
            font-size: var(--font-size-address);
            margin-bottom: 12px;
          }

          .title {
            color: var(--color-primary-dark);
            display: block;
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H2);
            line-height: 2;
          }

          .error.horizontal {
            visibility: hidden;
            margin-top: 3px;
            width: 100%;
          }

          .error.visible {
            visibility: visible;
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            margin-top: 14px;
            text-align: left;
            white-space: break-spaces;
            margin-right: ${(isHorizontal && !isRtl) ? 'auto' : 'unset'};
            margin-left: ${(isHorizontal && isRtl) ? 'auto' : 'unset'};
          }

          .label-title {
            color: var(--color-primary-dark);
            display: block;
            font-weight: var(--font-weight-H1);
            margin-bottom: 5px;
          }

          .wrapper.horizontal {
            flex-direction: row;
          }

          .wrapper.column-grid {
            display: grid;
            grid-template-columns: repeat(${columnCount || 1}, 1fr);
            gap: 8px 12px;
          }

          .wrapper.column-grid .small-title {
            grid-column: 1 / -1;
          }

          .wrapper.column-grid .radio-button {
            flex-direction: row;
            align-items: center;
            border-bottom: none;
            padding: 4px 0 4px 6px;
          }

          .wrapper.column-grid .button-wrapper {
            order: -1;
            margin-left: 0;
            margin-right: 12px;
            flex-shrink: 0;
          }

          .wrapper.column-grid .heading {
            max-width: none;
            margin-left: 0;
            flex: 1;
            justify-content: center;
          }

          .wrapper.column-grid .label-title {
            margin-bottom: 0;
            line-height: 1.2;
          }

          .radio-button {
            box-sizing: border-box;
            font-size: var(--font-size-body);
            position: relative;
            display: flex;
            flex-direction: ${showSubtitle && isHorizontal ? 'row-reverse' : 'row'};
            white-space: 'pre-wrap';
            cursor: pointer;
            padding: 8px 0 8px 4px;
            margin-right: 0;
            border-bottom: 1px solid var(--color-line-grey);
            line-height: ${showSubtitle && isHorizontal ? '20px' : '32px'};
            width: 100%;
          }
          
          .radio-button.is--disabled,
          .radio-button[disabled] {
            cursor: default;
            pointer-events: none;
            user-select: none;
            color: var(--color-mid-grey);
            
          }

          .radio-button.is--disabled .visual-button::after {
            position: absolute;
            background-color: var(--color-gray);
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            border-radius: 100%;
            content: "";
          }
          
          .wrapper.horizontal .radio-button {
            border-bottom: none;
          }

          .button-wrapper {
            right: 0;
            margin-left: auto;
          }

          .wrapper.horizontal .button-wrapper {
            margin-left: 0;
          }

          .button-wrapper.border {
            border-left: 1px solid var(--color-line-grey);
          }

          .visual-button {
            position: relative;
            top: 0;
            right: 0;
            display: block;
            width: 32px;
            height: 32px;
            background-color: var(--color-white);
            border: 1px solid var(--color-line-grey);
            border-radius: 100px;
            margin-left: ${showSubtitle && isHorizontal ? '' : '16px'};
          }

          .visual-button:hover {
            border-color: ${primaryColor};
          }

          .heading {
            margin-left: 15px;
            max-width: 250px;
            font-size: var(--font-size-H4);            
            display: flex;
            flex-direction: column;
          }

          .heading > div {
            overflow-wrap: break-word;
          }

          .radio-button.selected .visual-button::after {
            position: absolute;
            background-color: var(--color-primary-dark);
            width: 9px;
            height: 9px;
            top: 10.5px;
            left: 10.5px;
            border-radius: 10px;
            content: "";
          }

          .radio-button[disabled] .visual-button::after {
            background-color: var(--color-disabled-grey);
          }

          .sub-controls {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin: 10px 0;
          }

          @media only screen and (max-width: 600px) {
            .wrapper {
              gap: 0;
            }
            
            .wrapper.horizontal {
              flex-direction: ${showSubtitle ? 'column' : 'row'} ;
            }

          }
        `}
      </style>
    </>
  );
});

RadioButtons.defaultProps = {
  inputSelectorId: null,
  subControls: [],
  generateControl: null,
  onChange: null,
  defaultSelected: 0,
  isHorizontal: false,
  gapValue: 0,
  showLabels: true,
  showBorders: true,
  title: null,
  smallTitle: false,
  required: false,
  showSubtitle: false,
  dynamicSubcontrols: false,
  subTitleOptions: [],
  options: [],
  error: '',
  setContent: () => {},
  disabled: [],
  telemetryEvent: null,
  telemetryValue: null,
  telemetryCustomProperty: null,
  ariaLabel: null,
  columnCount: null,
};

RadioButtons.propTypes = {
  inputSelectorId: PropTypes.string,
  subControls: PropTypes.arrayOf(PropTypes.shape()),
  options: PropTypes.arrayOf(PropTypes.string.isRequired),
  subTitleOptions: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.string, subTitle: PropTypes.string })),
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool])).isRequired,
  generateControl: PropTypes.func,
  onChange: PropTypes.func,
  defaultSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isHorizontal: PropTypes.bool,
  gapValue: PropTypes.number,
  showLabels: PropTypes.bool,
  showBorders: PropTypes.bool,
  title: PropTypes.string,
  smallTitle: PropTypes.bool,
  required: PropTypes.bool,
  showSubtitle: PropTypes.bool,
  dynamicSubcontrols: PropTypes.bool,
  error: PropTypes.string,
  setContent: PropTypes.func,
  disabled: PropTypes.arrayOf(PropTypes.bool),
  telemetryEvent: PropTypes.string,
  telemetryValue: PropTypes.string,
  telemetryCustomProperty: PropTypes.string,
  ariaLabel: PropTypes.string,
  columnCount: PropTypes.number,
};

export default ErrorReportingHOC(RadioButtons);
